import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Button,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Fade,
  Grow
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import MessageIcon from '@mui/icons-material/Message';
import EventNoteIcon from '@mui/icons-material/EventNote';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import SchoolIcon from '@mui/icons-material/School';

// Styled components
const HighlightsContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4, 0),
  width: '100%'
}));

const FeatureCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8]
  }
}));

const FeatureIcon = styled(Box)(({ theme, color = 'primary' }) => ({
  backgroundColor: theme.palette[color].main,
  color: theme.palette[color].contrastText,
  borderRadius: '50%',
  padding: theme.spacing(1.5),
  display: 'inline-flex',
  marginBottom: theme.spacing(2)
}));

const TabPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3, 0)
}));

const VideoPreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  cursor: 'pointer',
  '&:hover .play-button': {
    opacity: 1,
    transform: 'translate(-50%, -50%) scale(1.1)'
  },
  '&:hover::after': {
    opacity: 0.7
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'black',
    opacity: 0.5,
    transition: 'opacity 0.3s ease'
  }
}));

const PlayButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  zIndex: 1,
  opacity: 0.9,
  transition: 'transform 0.3s ease, opacity 0.3s ease',
  padding: theme.spacing(2),
  '&:hover': {
    backgroundColor: theme.palette.primary.dark
  }
}));

const FeatureHighlights = ({ role = 'coach', onGetStarted }) => {
  const [activeTab, setActiveTab] = useState(0);
  
  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  // Features based on role
  const getFeatures = (role) => {
    const commonFeatures = [
      {
        title: 'Prospect Management',
        description: 'Track and manage all your recruiting prospects in one place with detailed profiles and customizable tags.',
        icon: <PeopleIcon fontSize="large" />,
        color: 'primary',
        image: '/assets/features/prospects.jpg'
      },
      {
        title: 'Communication Hub',
        description: 'Stay connected with your team and prospects through integrated messaging, email, and scheduling tools.',
        icon: <MessageIcon fontSize="large" />,
        color: 'secondary',
        image: '/assets/features/communication.jpg'
      },
      {
        title: 'Analytics Dashboard',
        description: 'Make data-driven decisions with comprehensive analytics and reporting on your recruiting efforts.',
        icon: <AssessmentIcon fontSize="large" />,
        color: 'info',
        image: '/assets/features/analytics.jpg'
      }
    ];
    
    const roleSpecificFeatures = {
      coach: [
        {
          title: 'Team Management',
          description: 'Manage your current roster, track scholarship allocations, and plan for future recruiting needs.',
          icon: <SportsSoccerIcon fontSize="large" />,
          color: 'success',
          image: '/assets/features/team.jpg'
        }
      ],
      recruiter: [
        {
          title: 'Recruiting Calendar',
          description: 'Plan your recruiting schedule with integrated calendar tools for visits, events, and deadlines.',
          icon: <EventNoteIcon fontSize="large" />,
          color: 'warning',
          image: '/assets/features/calendar.jpg'
        }
      ],
      admin: [
        {
          title: 'Program Oversight',
          description: 'Get a bird\'s-eye view of your entire program with administrative tools and permissions management.',
          icon: <SchoolIcon fontSize="large" />,
          color: 'error',
          image: '/assets/features/admin.jpg'
        }
      ]
    };
    
    return [...commonFeatures, ...(roleSpecificFeatures[role] || [])];
  };
  
  // Get features for the current role
  const features = getFeatures(role);
  
  // Tab content
  const tabs = [
    {
      label: 'Features',
      content: (
        <Grid container spacing={3}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Grow in={true} timeout={(index + 1) * 300}>
                <FeatureCard>
                  <CardContent>
                    <FeatureIcon color={feature.color}>
                      {feature.icon}
                    </FeatureIcon>
                    <Typography variant="h6" gutterBottom>
                      {feature.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                  <Box sx={{ mt: 'auto', p: 2, pt: 0 }}>
                    <Button
                      endIcon={<ArrowForwardIcon />}
                      size="small"
                    >
                      Learn more
                    </Button>
                  </Box>
                </FeatureCard>
              </Grow>
            </Grid>
          ))}
        </Grid>
      )
    },
    {
      label: 'Videos',
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <VideoPreview>
              <img
                src="/assets/videos/getting-started-thumbnail.jpg"
                alt="Getting Started"
                style={{ width: '100%', height: 'auto' }}
              />
              <PlayButton className="play-button">
                <PlayArrowIcon fontSize="large" />
              </PlayButton>
            </VideoPreview>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Getting Started with TeamScout
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A quick overview of the platform and how to get started.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <VideoPreview>
              <img
                src="/assets/videos/advanced-features-thumbnail.jpg"
                alt="Advanced Features"
                style={{ width: '100%', height: 'auto' }}
              />
              <PlayButton className="play-button">
                <PlayArrowIcon fontSize="large" />
              </PlayButton>
            </VideoPreview>
            <Typography variant="h6" sx={{ mt: 2 }}>
              Advanced Features for {role === 'coach' ? 'Coaches' : role === 'recruiter' ? 'Recruiters' : 'Administrators'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Learn about the powerful tools designed specifically for your role.
            </Typography>
          </Grid>
        </Grid>
      )
    },
    {
      label: 'Testimonials',
      content: (
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body1" paragraph>
                  "TeamScout has completely transformed our recruiting process. We're more organized, efficient, and effective than ever before."
                </Typography>
                <Typography variant="subtitle2">
                  John Smith
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Head Coach, State University
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body1" paragraph>
                  "The analytics tools have given us insights we never had before. We're making better decisions and finding the right talent for our program."
                </Typography>
                <Typography variant="subtitle2">
                  Sarah Johnson
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Recruiting Coordinator, Tech College
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="body1" paragraph>
                  "As an administrator, I can finally see the big picture of our recruiting efforts across all sports. The reporting tools are invaluable."
                </Typography>
                <Typography variant="subtitle2">
                  Michael Williams
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Athletic Director, Central University
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )
    }
  ];
  
  return (
    <HighlightsContainer>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome to TeamScout
        </Typography>
        <Typography variant="subtitle1" color="text.secondary" sx={{ maxWidth: 700, mx: 'auto' }}>
          The all-in-one platform for college recruiting and team management
        </Typography>
      </Box>
      
      <Paper sx={{ mb: 4 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          centered
          variant="fullWidth"
        >
          {tabs.map((tab, index) => (
            <Tab key={index} label={tab.label} />
          ))}
        </Tabs>
      </Paper>
      
      {tabs.map((tab, index) => (
        <TabPanel
          key={index}
          role="tabpanel"
          hidden={activeTab !== index}
        >
          <Fade in={activeTab === index} timeout={500}>
            <Box>{tab.content}</Box>
          </Fade>
        </TabPanel>
      ))}
      
      <Box sx={{ textAlign: 'center', mt: 6 }}>
        <Button
          variant="contained"
          size="large"
          color="primary"
          onClick={onGetStarted}
          endIcon={<ArrowForwardIcon />}
        >
          Get Started
        </Button>
      </Box>
    </HighlightsContainer>
  );
};

export default FeatureHighlights;
